/** Cantidad de filas iniciales que se inspeccionan buscando los encabezados. */
const SCAN_ROWS = 10;

/** Minimo de celdas llenas (respecto al ancho de la tabla) para considerar una fila encabezado. */
const MIN_FILL_RATIO = 0.6;

/** Minimo de celdas no numericas para considerar una fila encabezado. */
const MIN_TEXT_RATIO = 0.6;

export type HeaderDetection = {
    /** Indice de la fila de encabezados, o `null` si el archivo no parece tenerlos. */
    headerRowIndex: number | null;
    /** Cantidad de columnas de la tabla. */
    columnCount: number;
};

const isBlank = (cell: string): boolean => !`${cell ?? ""}`.trim();

const looksNumeric = (cell: string): boolean => {
    const value = `${cell ?? ""}`.trim();
    if (!value) return false;
    // Acepta separadores de miles y coma o punto decimal, que es lo que exportan
    // los sistemas de facturacion segun la configuracion regional.
    return /^[-+]?[\d.,\s]+$/.test(value) && /\d/.test(value);
};

const filledCells = (row: string[]): string[] => (row || []).filter((cell) => !isBlank(cell));

/** Ancho real de la tabla: el maximo de celdas llenas entre las filas inspeccionadas. */
export const detectColumnCount = (rows: string[][]): number =>
    rows.slice(0, SCAN_ROWS).reduce((max, row) => Math.max(max, (row || []).length), 0);

/**
 * Una fila esta "llena" si cubre buena parte del ancho de la tabla. Las filas de titulo
 * ("Lista de clientes" sola en 18 columnas) no lo estan, y por eso se pueden saltar.
 */
const isFullRow = (row: string[], columnCount: number): boolean => {
    const filled = filledCells(row);
    return filled.length >= 2 && filled.length >= columnCount * MIN_FILL_RATIO;
};

/**
 * Decide si una fila llena parece encabezado o ya son datos. Los nombres de las columnas
 * dependen del sistema que genero el archivo, asi que no se compara contra ninguna lista
 * de encabezados esperados: solo se mira la *forma* de la fila.
 */
const looksLikeHeader = (row: string[]): boolean => {
    const filled = filledCells(row);

    // Un encabezado no trae correos: eso ya es un dato.
    if (filled.some((cell) => cell.includes("@"))) return false;

    const textCells = filled.filter((cell) => !looksNumeric(cell));
    if (textCells.length < filled.length * MIN_TEXT_RATIO) return false;

    // Dos columnas no pueden llamarse igual.
    const distinct = new Set(filled.map((cell) => cell.trim().toLowerCase()));
    return distinct.size === filled.length;
};

/**
 * Propone cual fila contiene los encabezados.
 *
 * Los archivos vienen de sistemas externos: algunos abren con una fila de titulo
 * ("Lista de clientes") y los encabezados en la segunda, otros arrancan directo con los
 * encabezados, y otros no traen encabezados del todo. Devolver `null` en ese ultimo caso
 * es un resultado valido: la UI nombra las columnas `Columna1..ColumnaN`.
 *
 * Es solo una sugerencia; el usuario puede corregirla en el primer paso del asistente.
 */
export const detectHeaderRow = (rows: string[][]): HeaderDetection => {
    const columnCount = detectColumnCount(rows);
    if (!columnCount) {
        return { headerRowIndex: null, columnCount: 0 };
    }

    // Se salta lo que haya arriba (titulos, filas en blanco) hasta la primera fila llena.
    // Esa fila es o los encabezados o ya la primera fila de datos: si no parece encabezado
    // no hay que seguir bajando, porque lo unico que queda son datos.
    const scanned = rows.slice(0, SCAN_ROWS);
    const firstFullRowIndex = scanned.findIndex((row) => isFullRow(row, columnCount));

    if (firstFullRowIndex === -1 || !looksLikeHeader(scanned[firstFullRowIndex])) {
        return { headerRowIndex: null, columnCount };
    }

    return { headerRowIndex: firstFullRowIndex, columnCount };
};

/** Nombre generico de una columna cuando el archivo no trae encabezados. */
export const genericColumnName = (index: number): string => `Columna${index + 1}`;

/**
 * Nombres de columna finales a partir de la fila de encabezados elegida.
 * Rellena los huecos: una celda vacia en medio de los encabezados igual necesita nombre.
 */
export const buildColumnNames = (
    rows: string[][],
    headerRowIndex: number | null,
    columnCount: number
): string[] => {
    const headerRow = headerRowIndex === null ? [] : rows[headerRowIndex] || [];

    return Array.from({ length: columnCount }, (_, index) => {
        const raw = `${headerRow[index] ?? ""}`.trim();
        return raw || genericColumnName(index);
    });
};

/** Filas de datos: todo lo que viene despues de los encabezados, sin filas totalmente vacias. */
export const buildDataRows = (rows: string[][], headerRowIndex: number | null): string[][] =>
    rows.slice(headerRowIndex === null ? 0 : headerRowIndex + 1).filter((row) => filledCells(row).length > 0);
